import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';

import { Place } from '@app/modules/model/place';
import { Waiter } from '@app/modules/model/waiter';
import { User } from '@app/modules/model/user';
import { UiWaiterPin } from '@app/components/common/waiter-pin/ui-waiter-pin';

import { alertService } from '@app/modules/common/alert';
import { languageService, languageSupport } from '@app/modules/common/language';
import { dataService } from '@app/modules/data';
import { syncService } from '@app/modules/sync';
import { viewService } from '@app/modules/view';
import { waiterLockService } from '@app/modules/waiter-lock.service';
import { keyboardWedgeService } from '@app/modules/common/keyboard-wedge';
import { AlertModalController } from '@app/modules/common/alert';
import { rfidAssignHelper } from '@app/modules/common/rfid-assign';

/************************************/
/* USER COMPONENT                   */
/************************************/

@Component({
    selector: 'ui-waiter-edit',
    templateUrl: 'ui-edit.html',
    styleUrls: [
      'ui-edit.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiWaiterEdit extends languageSupport  implements OnInit, OnChanges, OnDestroy {    
    @Input('place') _place: Place = null; 
    @Input('waiter') _waiter: Waiter = null; 
    @Input('user') _user: User = null; 

    get NewWaiter(){
        return this._waiter.ToInsert;
    }

    get OnlineStatus(){
        if (this._waiter.user.IsPending){
            return 'pending';
        }

        return (this._waiter.IsOnline) ? 'online' : 'offline';
    }

    // ¿Puede el usuario EFECTIVO (this._user = data.EffectiveUser, el camarero del PIN, no
    // la cuenta de sesión) gestionar staff? Owner del local o flag StaffManage. La
    // autorización de staff es CLIENTE: el server solo ve el usuario de SESIÓN (normalmente
    // el owner) y no distingue qué camarero está identificado en el dispositivo, así que un
    // camarero sin permiso NO debe poder crear/editar fichas ni poner PIN a otros.
    get CanManageStaff(): boolean {
        if (!this._place || !this._user){
            return false;
        }
        // AllowStaffManage ya concede al owner REAL (chequeo por objid en Place._isOwner) y
        // niega principales desconocidos/volátiles. NO usar IsOwner: devuelve true para
        // CUALQUIER usuario que no case en waiters (incluido el sentinel de bloqueo y objetos
        // stale), lo que saltaría el gate con la pantalla bloqueada.
        return this._place.AllowStaffManage(this._user);
    }

    // ¿Es la PROPIA ficha del usuario efectivo? (autoservicio: cambiar su propio PIN).
    // Exige AMBOS objid presentes: dos ids ausentes (ficha NUEVA con User(null), o el
    // principal de bloqueo volátil sin objid) NO son "el mismo usuario" (null == null sería
    // true y saltaría el gate de creación/bloqueo).
    get IsSelf(): boolean {
        let _wid = (this._waiter && this._waiter.user) ? this._waiter.user.objid : null;
        let _uid = this._user ? this._user.objid : null;
        return !!(_wid && _uid && (_wid == _uid));
    }

    // Puede tocar el PIN de ESTA ficha: gestiona staff, o es la suya (autoservicio de PIN).
    // La edición GENERAL (alias/email/foto/permisos) es solo-manager (ver OnAccept).
    get CanChangePin(): boolean {
        return this.CanManageStaff || this.IsSelf;
    }

    get CanRemove(){
        if (!this.CanManageStaff){
            return false;   // dar de baja a un camarero es gestión: solo owner/StaffManage
        }
        if (this._waiter.IsValid && !this._waiter.ToInsert){
            return true;
        }
    }

    CanSetPermmision(value){
        if (this._waiter.user == this._user){
            return false;   // cannot modify own permissions

        }

        let _reference = null;
        for(let _waiter of this._place.waiters){
            if (_waiter.IsValid && (_waiter.user == this._user)){
                _reference = _waiter;
            }
        }

        if (!_reference){
            return true;    // this is the place owner
        }
        else {
            switch (value){
                case 'StaffManage': return _reference.AllowStaffManage;
                case 'OfferManage': return _reference.AllowOfferManage;
                case 'ProductManage': return _reference.AllowOfferManage;
                case 'TableManage': return _reference.AllowTableManage;
                case 'ExtraManage': return _reference.AllowExtraManage;
                case 'PlaceManage': return _reference.AllowPlaceManage;
                case 'LicenceManage': return _reference.AllowLicenceManage;
                case 'CanCompliment': return _reference.AllowCompliment;
                case 'CanAvailable': return _reference.AllowAvailability;
                case 'POSManage': return _reference.AllowPOSManage;
                case 'ReportAccess': return _reference.AllowReports;
                case 'TillManage': return _reference.AllowTillManage;
                case 'CanReopen': return _reference.AllowTicketReopen;
                case 'CanCancel': return _reference.AllowTicketCancel;
                case 'CanModify': return _reference.AllowTicketUpdate;
                case 'CanReturn': return _reference.AllowTicketReturn;
                case 'ClientAdd': return _reference.AllowClientAdd;
            }

            console.warn("[WAITER] Unknown permission value '" + value + "'")
            return false;   // permission not found
        }
    }

    /**********************************/
    /* WAITER VIEW                    */
    /**********************************/

    private _nophoto = '/assets/image/waiter.png';

    private _waiterForm = null;   
    get WaiterForm(){
        if (this._waiterForm == null){
            this.LoadWaiterForm();
        }
        return this._waiterForm;
    }

    private checkExistingWaiter(control: FormControl): any {
        let _errors = null;

        let _waitermail = control.value?.toLowerCase() || null;
        if (_waitermail){
            // check that is not the place owner
            if (_waitermail == this._user.email?.toLowerCase() || null){
                _errors = { 'owner': true };
            }

            // check that is not an exising waiter
            else {
                if (_waitermail != this._waiter.email?.toLowerCase() || null){
                    for(let _waiter of this._place.waiters){
                        if ((_waiter.IsValid) && ((_waiter.email?.toLowerCase() || null) == _waitermail)){
                            _errors = { 'exists': true };
                        }
                    }        
                }
            }
        }
        
        return _errors;
    }
    
    private checkExistingAlias(control: FormControl): any {
        let _errors = null;

        let _alias = control.value;
        if (_alias){
            // check that is not an exising waiter
            if (_alias != this._waiter.alias){
                for(let _waiter of this._place.waiters){
                    if ((_waiter.IsValid) && (_waiter.alias == _alias)){
                        _errors = { 'exists': true };
                    }
                }        
            }
        }
        
        return _errors;
    }

    // Unicidad del PIN: la valida el SERVER (spec/staff.php), el cliente ya no
    // tiene los PIN/hashes. Si el server la rechaza, el commit falla.

    // Ajusta qué campos son editables según el permiso EFECTIVO. Para un usuario que NO
    // gestiona staff, deshabilita TODOS los campos de edición general (datos y permisos)
    // dejando SOLO el PIN editable (autoservicio): así el form solo se ensucia cambiando el
    // PIN -> Guardar se habilita solo entonces, y no se permiten ni se descartan en silencio
    // cambios de otros campos. Es BIDIRECCIONAL: el FormGroup se reutiliza entre
    // lock/identify (ngOnChanges no lo recrea), así que para un manager hay que RE-habilitar
    // (si no, un manager que se identifica tras un bloqueo quedaría con los campos
    // deshabilitados) y reaplicar las dependencias Extra/Till.
    private _applyEditableState(){
        if (!this._waiterForm){
            return;
        }
        let _general = [
            'photo', 'alias', 'firstname', 'lastname', 'email', 'phone',
            'allowStaffManage', 'allowOfferManage', 'allowProductManage', 'allowTableManage',
            'allowExtraManage', 'allowPlaceManage', 'allowLicenceManage', 'allowCanCompliment',
            'allowCanAvailable', 'allowPOSManage', 'allowReportAccess', 'allowTillManage',
            'allowTillOpen', 'allowCanReopen', 'allowCanCancel', 'allowCanModify',
            'allowCanReturn', 'allowClientAdd'
        ];
        let _manage = this.CanManageStaff;
        for(let _name of _general){
            let _ctrl = this._waiterForm.get(_name);
            if (_ctrl){
                if (_manage){
                    _ctrl.enable({ emitEvent: false });
                }
                else {
                    _ctrl.disable({ emitEvent: false });
                }
            }
        }
        if (_manage){
            // Reaplicar dependencias tras re-habilitar (Extra depende de TableManage;
            // TillOpen de TillManage).
            this.OnAllowTableManage();
            this.OnAllowTillManage();
        }
        // 'pin' queda editable (OpenPinPad lo gatea por CanChangePin).
    }

    private LoadWaiterForm(){
        let _photo = this._waiter.photo || this._nophoto;
        let _alias = this._waiter.alias;
        let _email = this._waiter.email;
        let _pin = '';      // el PIN no se muestra (el server nunca lo devuelve);
                            // el input es para PONER uno nuevo. has_pin indica si hay.
        let _firstname = this._waiter.user.firstname;
        let _lastname = this._waiter.user.lastname;
        let _phone = this._waiter.user.phone;

        let _allow = {
            StaffManage: this._waiter.AllowStaffManage,
            OfferManage: this._waiter.AllowOfferManage,
            ProductManage: this._waiter.AllowProductManage,
            TableManage: this._waiter.AllowTableManage,
            ExtraManage: this._waiter.AllowExtraManage,
            PlaceManage: this._waiter.AllowPlaceManage,
            LicenceManage: this._waiter.AllowLicenceManage,
            CanCompliment: this._waiter.AllowCompliment,
            CanAvailable: this._waiter.AllowAvailability,
            POSManage: this._waiter.AllowPOSManage,
            ReportAccess: this._waiter.AllowReports,
            TillManage: this._waiter.AllowTillManage,
            TillOpen: this._waiter.AllowTillOpen,
            CanReopen: this._waiter.AllowTicketReopen,
            CanCancel: this._waiter.AllowTicketCancel,
            CanModify: this._waiter.AllowTicketUpdate,
            CanReturn: this._waiter.AllowTicketReturn,
            ClientAdd: this._waiter.AllowClientAdd
        };

        if (this._waiterForm == null){
            this._waiterForm = new FormGroup({
                photo: new FormControl(_photo, [
                    // no validators required
                ]),
                alias: new FormControl(_alias, [
                    Validators.required,
                    this.checkExistingAlias.bind(this)
                ]),
                firstname: new FormControl(_firstname, [ 
                    // no validators
                ]),
                lastname: new FormControl(_lastname, [ 
                    // no validators
                ]),
                email: new FormControl(_email, [
                    Validators.required,
                    Validators.email,
                    this.checkExistingWaiter.bind(this)
                ]),
                phone: new FormControl(_phone, [
                    // no validators
                ]),
                pin: new FormControl(_pin, [
                    // Contrato ÚNICO de PIN (igual que el modal UiWaiterPin): 4-8
                    // dígitos, opcional (vacío = no cambiar). La UNICIDAD la valida
                    // el server (el cliente ya no tiene los PIN/hashes).
                    Validators.pattern(/^[0-9]*$/),     // solo dígitos
                    Validators.minLength(4),
                    Validators.maxLength(8)
                ]),

                allowStaffManage: new FormControl(_allow.StaffManage, [
                    // no validators
                ]),
                allowOfferManage: new FormControl(_allow.OfferManage, [
                    // no validators
                ]),
                allowProductManage: new FormControl(_allow.ProductManage, [
                    // no validators
                ]),
                allowTableManage: new FormControl(_allow.TableManage, [
                    // no validators
                ]),
                allowExtraManage: new FormControl(_allow.ExtraManage, [
                    // no validators
                ]),
                allowPlaceManage: new FormControl(_allow.PlaceManage, [
                    // no validators
                ]),
                allowLicenceManage: new FormControl(_allow.LicenceManage, [
                    // no validators
                ]),
                allowCanCompliment: new FormControl(_allow.CanCompliment, [
                    // no validators
                ]),
                allowCanAvailable: new FormControl(_allow.CanAvailable, [
                    // no validators
                ]),
                allowPOSManage: new FormControl(_allow.POSManage, [
                    // no validators
                ]),
                allowReportAccess: new FormControl(_allow.ReportAccess, [
                    // no validators
                ]),
                allowTillManage: new FormControl(_allow.TillManage, [
                    // no validators
                ]),
                allowTillOpen: new FormControl(_allow.TillOpen, [
                    // no validators
                ]),
                allowCanReopen: new FormControl(_allow.CanReopen, [
                    // no validators
                ]),
                allowCanCancel: new FormControl(_allow.CanCancel, [
                    // no validators
                ]),
                allowCanModify: new FormControl(_allow.CanModify, [
                    // no validators
                ]),
                allowCanReturn: new FormControl(_allow.CanReturn, [
                    // no validators
                ]),
                allowClientAdd: new FormControl(_allow.ClientAdd, [
                    // no validators
                ])
            });
        }
        else {
            this._waiterForm.patchValue({
                photo: _photo,
                alias: _alias,
                firstname: _firstname,
                lastname: _lastname,
                email: _email,
                phone: _phone,
                pin: _pin,

                allowStaffManage: _allow.StaffManage,
                allowOfferManage: _allow.OfferManage,
                allowProductManage: _allow.ProductManage,
                allowTableManage: _allow.TableManage,
                allowExtraManage: _allow.ExtraManage,
                allowPlaceManage: _allow.PlaceManage,
                allowLicenceManage: _allow.LicenceManage,
                allowCanCompliment: _allow.CanCompliment,
                allowCanAvailable: _allow.CanAvailable,
                allowPOSManage: _allow.POSManage,
                allowReportAccess: _allow.ReportAccess,
                allowTillManage: _allow.TillManage,
                allowTillOpen: _allow.TillOpen,
                allowCanReopen: _allow.CanReopen,
                allowCanCancel: _allow.CanCancel,
                allowCanModify: _allow.CanModify,
                allowCanReturn: _allow.CanReturn,
                allowClientAdd: _allow.ClientAdd
            });
        }

        this.OnAllowTableManage();
        this.OnAllowTillManage();

        this._applyEditableState();

        this._waiterForm.markAsPristine();
        setTimeout(() => {
            this._waiterForm.markAsPristine();
        }, 0);
    }

    OnViewChanged(){
        this.change.markForCheck();
    }

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private data: dataService, public view: viewService, private modalCtrl: ModalController, private waiterLock: waiterLockService, private wedge: keyboardWedgeService, private sync: syncService) {
        super(lang, change);
    }

    // PIN por NUMPAD (mismo que el bloqueo): se abre el teclado numérico, se teclea
    // enmascarado con '*' y se guarda en el control 'pin' (lo envía OnAccept). No se
    // muestra el PIN actual (el server nunca lo devuelve): solo has_pin.
    get PinDisplay(): string {
        let _v = this.WaiterForm.get('pin').value;
        if (_v){
            return '•'.repeat(('' + _v).length);
        }
        return this._waiter.has_pin ? this.tr('@place_waiter_pin_set') : this.tr('@place_waiter_input_pin_ph');
    }

    _oldPin: string = null;     // PIN actual tecleado en el cambio (se envía al server)

    // PULSERA RFID: tag capturado del lector (pendiente de guardar) o petición de
    // quitarla. Igual que el PIN, el tag actual nunca se muestra (el server no lo
    // devuelve): solo has_rfid.
    _newRfid: string = null;
    _clearRfid = false;
    _rfidReassign = false;      // la pulsera nueva se le quita a quien la tenía (confirmado)
    private _rfidAlert: AlertModalController = null;

    get HasRfid(): boolean {
        if (this._clearRfid){
            return false;
        }
        return !!(this._newRfid || (this._waiter && this._waiter.has_rfid));
    }

    get RfidDisplay(): string {
        if (this._newRfid){
            return this.tr('@place_waiter_rfid_captured');
        }
        if (this._clearRfid){
            return this.tr('@place_waiter_rfid_ph');
        }
        return (this._waiter && this._waiter.has_rfid) ? this.tr('@place_waiter_rfid_set') : this.tr('@place_waiter_rfid_ph');
    }

    // Asignar pulsera: se abre un aviso y se espera la SIGUIENTE lectura del lector.
    // Captura EXCLUSIVA: mientras está pendiente el TPV (ui-place) no recibe la
    // lectura, así no identifica/cambia de camarero con la pulsera que se asigna.
    async OpenRfidCapture(){
        if (!this.CanChangePin || this._rfidAlert){
            return;
        }
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_waiter_rfid_capture_title'),
            message: this.lang.tr('@place_waiter_rfid_capture_message'),
            backdropDismiss: true,
            buttons: [{
                text: this.lang.tr('@place_oneclick_cancel'),
                role: 'cancel'
            }]
        });
        this._rfidAlert = alert;
        this.wedge.Capture().then(async (tag: string) => {
            this._closeRfidCapture();
            if (tag){
                // Mientras se comprueba/confirma, el lector sigue reemitiendo la pulsera
                // que está encima: las relecturas NO deben identificar/cambiar el camarero
                // del TPV por detrás (SuppressTags hasta terminar).
                this.wedge.SuppressTags(true);
                try {
                    // ¿Ya la tiene otro? Lo resuelve el server (el cliente no ve los tags) y
                    // dice si quien edita puede quitársela. Si no se puede comprobar
                    // (offline), se sigue y el server valida al guardar.
                    let _r = await rfidAssignHelper.Resolve(this.sync, this.lang, this.alertCtrl, this._place.objid, (this._waiter && this._waiter.objid) || 0, 'staff', tag);
                    if (_r.assign){
                        this._newRfid = tag;
                        this._clearRfid = false;
                        this._rfidReassign = _r.reassign;
                        this.WaiterForm.markAsDirty();
                    }
                }
                finally {
                    this.wedge.SuppressTags(false);
                }
            }
            this.change.markForCheck();
        });
        alert.onDidDismiss().then(() => {
            this.wedge.CancelCapture();     // resuelve la captura con null (cierra arriba)
        });
        await alert.present();
    }

    private _closeRfidCapture(){
        if (this._rfidAlert){
            let _alert = this._rfidAlert;
            this._rfidAlert = null;
            _alert.dismiss(null).catch(() => {});
        }
    }

    OnClearRfid(event: Event){
        if (event){
            event.stopPropagation();
        }
        if (!this.CanChangePin){
            return;
        }
        this._newRfid = null;
        this._clearRfid = true;
        this._rfidReassign = false;
        this.WaiterForm.markAsDirty();
        this.change.markForCheck();
    }

    async OpenPinPad(){
        // Gate de permiso EFECTIVO: poner/cambiar el PIN de OTRO camarero exige gestionar
        // staff; la propia ficha se permite (autoservicio). Sin esto, un camarero sin
        // permiso podría setear el PIN de otro (el server ve la sesión del owner y lo acepta).
        if (!this.CanChangePin){
            return;
        }
        // Pide el PIN actual SOLO si es la PROPIA ficha del operario (cambio propio)
        // y ya hay PIN. "Propia" se mide contra data.user (el USER AUTENTICADO de la
        // sesión), que es lo que verifica el server — NO el usuario efectivo del PIN.
        // El owner/encargado que RESETEA el de otro no lo necesita.
        let _own = this._waiter.has_pin && this._waiter.user && this.data.user
                 && (this._waiter.user.objid == this.data.user.objid);
        const _modal = await this.modalCtrl.create({
            component: UiWaiterPin,
            componentProps: {
                place: this._place, changeMode: true, needsOld: !!_own, lockMode: false,
                // Aviso temprano de PIN ya en uso en el local (el server lo resuelve,
                // el cliente no tiene hashes). Se excluye la propia ficha para no
                // marcar su PIN actual como "cogido".
                checkPlace: this._place ? this._place.objid : null,
                checkExclude: this._waiter ? this._waiter.objid : 0
            },
            cssClass: 'waiter-pin-modal'
        });
        // Si el bloqueo saltó durante el await de create (antes de enganchar el
        // listener de abajo), no presentar el numpad.
        if (this.waiterLock.IsLocked){
            await _modal.dismiss(null).catch(() => {});
            return;
        }
        // Si salta el bloqueo por inactividad con el numpad abierto, cerrarlo: si no,
        // quedaría APILADO bajo el modal de bloqueo, ambos UiWaiterPin recibirían el
        // teclado y la confirmación del bloqueo podría caer en el numpad de captura.
        let _lockSub = this.waiterLock.OnLockRequired.subscribe(() => {
            _modal.dismiss(null).catch(() => {});
        });
        _modal.onDidDismiss().then((detail: any) => {
            _lockSub.unsubscribe();
            this.waiterLock.SetModalActive(false);
            let _pin = (detail && detail.data) ? detail.data.pin : null;
            if (_pin){
                this.WaiterForm.get('pin').setValue(_pin);
                this._oldPin = (detail.data.old) ? detail.data.old : null;
                this.WaiterForm.markAsDirty();
                this.change.markForCheck();
            }
        });
        this.waiterLock.SetModalActive(true);
        await _modal.present();
    }

    // Vuelca la pulsera capturada / la petición de quitarla al modelo (se envían al server).
    private _applyRfidChanges(){
        if (this._newRfid){
            this._waiter.rfid = this._newRfid;
            this._waiter.rfidreassign = this._rfidReassign;
        }
        else if (this._clearRfid){
            this._waiter.clearrfid = true;
        }
        this._newRfid = null;
        this._clearRfid = false;
        this._rfidReassign = false;
    }

    private _CanClose(){
        return new Promise(async (resolve) => {
            let _form = this.WaiterForm;
            if (_form.dirty){
                console.log("[EDIT WAITER] confirm changes lost");
                const alert = this.alertCtrl.alert({
                    header: this.lang.tr('@changes_lost_confirm_tittle'),
                    message: this.lang.tr('@changes_lost_confirm_message'),
                    backdropDismiss: false,
                    buttons: [{
                        text: this.lang.tr('@yes'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(true);
                            }, 100);
    
                            return false;
                        }
                    }, {
                        text: this.lang.tr('@no'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(false);
                            }, 100);
    
                            return false;
                        }
                    }]
                });
    
                alert.onDidDismiss().then((detail:any) => {
                    if (detail.data){
                        _form.markAsPristine();
                    }
                    
                    resolve(detail.data);
                });
    
                await alert.present();        
            }
            else {
                resolve(true);
            }
        });
    }

    private _close_subscription = null;
    private _theme_subscription = null;

    ngOnInit(){
        this._close_subscription = this.view.OnCloseRequest.subscribe(
        async () => {
            this.view.OnCloseResponse(await this._CanClose());
        });

        this._theme_subscription = this.view.OnTheme.subscribe(
        data => {
            this.change.markForCheck();
        });
    }

    private _subscriptions = [];
    ngOnChanges(){
        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];

        this.LoadWaiterForm();

        if (this._waiter){
            this._subscriptions.push(   // refresh on waiter change
                this._waiter.OnRefresh.subscribe(
                data => {
                    this.change.markForCheck();
                })
            );    

            if (this._waiter.place){
                this._subscriptions.push(   // refresh on place changes
                    this._waiter.place.OnTargetRefresh('STAFF').subscribe(
                    data => {
                        this.change.markForCheck();
                    })
                );        
            }

            if (this._waiter.user){
                this._subscriptions.push(   // refresh on user changes
                    this._waiter.user.OnRefresh.subscribe(
                    data => {
                        this.change.markForCheck();
                    })
                );    
            }
            
            this._subscriptions.push(   // refresh every minute (for online check)
                this.data.Clock.OnOneMinuteTick.subscribe(
                data => {
                    this.change.markForCheck();
                })
            );            
        }
    }

    ngOnDestroy(){
        super.OnDestroy();
        this._closeRfidCapture();

        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }

        if (this._theme_subscription){
            this._theme_subscription.unsubscribe();
            this._theme_subscription = null;
        }

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    private _OnClose(){
        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }

        if (this.view.RightPanel){
            this._onPanel.emit({
                scnd: null,
                args: null
            });    
        }
        else {
            this._onPanel.emit({
                scnd: null
            });

            // wait for animation to finish
            setTimeout(() => {
                this._onPanel.emit({
                    args: null
                });    
            }, 300);
        }
    }

    async OnRemove(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_waiter_delete_confirm'),
            message: this.lang.tr('@place_waiter_delete_message', [ this._waiter.user.fullname || this.lang.tr('@place_waiter_anonymous')]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then(async (detail:any) => {
            if (detail.data == true){
                this._waiter.status = 'DE';
                this._waiter.DoCommit();

                this.WaiterForm.markAsPristine();
                this.OnClose();
            }
        });

        await alert.present();
    }

    private async _ActivationMailAlert(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@user_activation_mail_sent_title'),
            message: this.lang.tr('@user_activation_mail_sent_message', [ this._waiter.user.email ]),
            backdropDismiss: true,
            buttons: [{
                text: this.lang.tr('@ok'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);

                    return false;
                }
            }]
        });

        await alert.present();        
    }

    async OnActivate(){
        this.data.DoActivate(this._waiter.user.email).then(
        result => {
            if (result){
                this._ActivationMailAlert();
            }
            else {
                console.warn("[WAITER] Error sending mail to user with email '" + this._waiter.user.email + "'")
            }
        })
    }

    private async _CommitWaiter(){
        this._waiter.user.email = this._waiter.email;
        
        let _transaction = await this.data.BeginTransaction();
        if (_transaction){
            _transaction.SendCommit(this._waiter.user);
            _transaction.SendCommit(this._waiter);
            
            return await _transaction.Flush(true);
        }
        
        return false;
    }

    OnAllowTableManage(){
        if (this.WaiterForm.get('allowTableManage').value){
            this.WaiterForm.get('allowExtraManage').enable();
        }
        else {
            this.WaiterForm.get('allowExtraManage').setValue(false);
            this.WaiterForm.get('allowExtraManage').disable();
        }
    }

    OnAllowTillManage(){
        if (this.WaiterForm.get('allowTillManage').value){
            this.WaiterForm.get('allowTillOpen').setValue(true);
            this.WaiterForm.get('allowTillOpen').disable();
        }
        else {
            this.WaiterForm.get('allowTillOpen').setValue(true);
            this.WaiterForm.get('allowTillOpen').enable();
        }
    }

    private async OnMailChanged(newmail){
        return new Promise((resolve) => {
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@user_data_email_changed_title'),
                message: this.lang.tr('@user_data_email_changed_message', [ newmail ]),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@ok'),
                    handler: () => {
                        this._waiter.email = newmail;
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
    
                        return false;
                    }
                }]
            });
    
            alert.onDidDismiss().then(
            (data) => {
                resolve(true);
            });

            alert.present();            
        })
    }

    async OnAccept(){
        // Gate de permiso EFECTIVO (defensa real, el server ve la sesión del owner).
        // AUTOSERVICIO: un no-manager solo puede cambiar SU propio PIN —no alias, email,
        // foto ni permisos—. La gestión COMPLETA (de cualquier ficha) es solo-manager.
        if (!this.CanManageStaff){
            if (!this.IsSelf){
                this.OnClose();     // ni gestiona staff ni es su ficha: nada que guardar
                return;
            }
            // Persistir SOLO el PIN; descartar cualquier cambio de los demás campos.
            let _selfpin = this.WaiterForm.get('pin').value;
            if (_selfpin){
                this._waiter.pin = _selfpin;
                this._waiter.oldpin = this._oldPin;     // el server verifica el actual
            }
            this._oldPin = null;
            this._applyRfidChanges();

            this.data.Clock.ClockEnable('waiter-accept', false);
            try {
                // AWAIT: _CommitWaiter es async; sin await el Promise siempre es truthy y
                // cerraría/limpiaría el form aunque el commit fallara. En fallo, dejar el
                // form dirty para que se pueda reintentar.
                let _selfcommit = await this._CommitWaiter();
                if (_selfcommit){
                    this.WaiterForm.markAsPristine();
                    this._place.AddWaiter(this._waiter);
                    this.OnClose();
                }
            }
            finally {
                this.data.Clock.ClockEnable('waiter-accept', true);
            }
            return;
        }

        let _newmail = this.WaiterForm.get('email').value;
        if (this._waiter.email == _newmail){
            _newmail = null;
        }

        this._waiter.email = this.WaiterForm.get('email').value;
        this._waiter.alias = this.WaiterForm.get('alias').value;
        // PIN: solo si el operario ha escrito uno NUEVO (vacío = no tocar -> el
        // server preserva el existente). El server lo hashea y valida unicidad.
        let _newpin = this.WaiterForm.get('pin').value;
        if (_newpin){
            this._waiter.pin = _newpin;
            this._waiter.oldpin = this._oldPin;     // el server verifica el actual (cambio propio)
        }
        this._oldPin = null;
        this._applyRfidChanges();
        this.WaiterForm.get('email').setValue(this._waiter.email);

        this._waiter.photo = this.WaiterForm.get('photo').value;
        if (this._waiter.photo == this._nophoto){
            this._waiter.photo = this._waiter.base64 = null;
        }

        this._waiter.AllowStaffManage = this.WaiterForm.get('allowStaffManage').value;
        this._waiter.AllowOfferManage = this.WaiterForm.get('allowOfferManage').value;
        this._waiter.AllowProductManage = this.WaiterForm.get('allowProductManage').value;
        this._waiter.AllowTableManage = this.WaiterForm.get('allowTableManage').value;
        this._waiter.AllowExtraManage = this.WaiterForm.get('allowExtraManage').value;
        this._waiter.AllowPlaceManage = this.WaiterForm.get('allowPlaceManage').value;
        this._waiter.AllowLicenceManage = this.WaiterForm.get('allowLicenceManage').value;
        this._waiter.AllowCompliment = this.WaiterForm.get('allowCanCompliment').value;
        this._waiter.AllowAvailability = this.WaiterForm.get('allowCanAvailable').value;
        this._waiter.AllowPOSManage = this.WaiterForm.get('allowPOSManage').value;
        this._waiter.AllowReports = this.WaiterForm.get('allowReportAccess').value;
        this._waiter.AllowTillManage = this.WaiterForm.get('allowTillManage').value;
        this._waiter.AllowTillOpen = this.WaiterForm.get('allowTillOpen').value;
        this._waiter.AllowTicketReopen = this.WaiterForm.get('allowCanReopen').value;
        this._waiter.AllowTicketCancel = this.WaiterForm.get('allowCanCancel').value;
        this._waiter.AllowTicketUpdate = this.WaiterForm.get('allowCanModify').value;
        this._waiter.AllowTicketReturn = this.WaiterForm.get('allowCanReturn').value;
        this._waiter.AllowClientAdd = this.WaiterForm.get('allowClientAdd').value;

        if (_newmail){
            await this.OnMailChanged(_newmail);     
        }

        this.data.Clock.ClockEnable('waiter-accept', false);     // pause all view refreshes
        let _commited = this._CommitWaiter();
        this.WaiterForm.markAsPristine();
        this._place.AddWaiter(this._waiter);
        this.data.Clock.ClockEnable('waiter-accept', true)      // resume all view refreshes

        if (_commited){
            this.OnClose();
        }
    }

    @Output('onPanel') _onPanel = new EventEmitter<any>();     
    async OnClose(){
        if (this.WaiterForm.dirty){
            console.log("[EDIT WAITER] confirm changes lost");
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@changes_lost_confirm_tittle'),
                message: this.lang.tr('@changes_lost_confirm_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);

                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);

                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._OnClose();
                }
            });

            await alert.present();        
        }
        else {
            this._OnClose();
        }
    }
}